/**
 * Lowest-confidence node/edge list for the Settings "Low confidence" panel.
 * Filters on the denormalized top-level `confidence` property (ingest writes
 * it via flattenProvenance); entities without confidence are excluded.
 */

import { asString, mapCypherNodeIdentity } from './cypher-row'

/** Default threshold for the panel and Advanced hub badge (80%). */
export const DEFAULT_LOW_CONFIDENCE_THRESHOLD = 0.8

/** Default top-X limit for the panel and Advanced hub badge. */
export const DEFAULT_LOW_CONFIDENCE_LIMIT = 50

/**
 * Explicit `LIMIT` because this panel wants a bounded top-X — no SKIP
 * drain (unlike flagged nodes).
 */
export const LOW_CONFIDENCE_NODES_CYPHER = `
MATCH (n)
WHERE n.confidence IS NOT NULL AND n.confidence < $threshold
RETURN n.id AS id, elementId(n) AS element_id, n.name AS name, labels(n) AS labels,
       n.confidence AS confidence, n.tag_path AS tag_path, n.folder_name AS folder_name
ORDER BY n.confidence ASC, toLower(coalesce(n.name, n.id, ''))
LIMIT toInteger($limit)
`.trim()

export const LOW_CONFIDENCE_RELATIONSHIPS_CYPHER = `
MATCH (a)-[r]->(b)
WHERE r.confidence IS NOT NULL AND r.confidence < $threshold
RETURN elementId(r) AS element_id, type(r) AS type, r.confidence AS confidence,
       coalesce(a.id, elementId(a)) AS source_id, coalesce(b.id, elementId(b)) AS target_id,
       coalesce(a.name, a.id) AS source_name, coalesce(b.name, b.id) AS target_name
ORDER BY r.confidence ASC
LIMIT toInteger($limit)
`.trim()

export interface LowConfidenceQueryArgs {
  /** Inclusive upper bound exclusive filter: confidence < threshold. Range [0, 1]. */
  threshold: number
  /** Max rows to return (top X lowest). */
  limit: number
}

/** One row on the low-confidence nodes list. */
export interface LowConfidenceNode {
  /** Business id when present, else the Neo4j element id. */
  id: string
  name?: string
  labels: string[]
  confidence: number
  /** Browse context: `tag_path` when present, else `folder_name`. */
  path?: string
}

/** One row on the low-confidence relationships list. */
export interface LowConfidenceRelationship {
  id: string
  type: string
  confidence: number
  /** Endpoint business id (or element id) for plant-view deep links. */
  sourceId?: string
  targetId?: string
  sourceName?: string
  targetName?: string
}

/** Body for POST /relationships/evidence. */
export interface RecordRelationshipEvidenceArgs {
  relationshipId: string
  note: string
  valid?: boolean
  needsReview?: boolean
}

export interface RecordRelationshipEvidenceResult {
  relationship?: Record<string, unknown>
  counters?: Record<string, number>
}

function asConfidence(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return value
}

/** Map one node cypher row; null when id or confidence is unusable. */
export function mapLowConfidenceNodeRow(row: Record<string, unknown>): LowConfidenceNode | null {
  const identity = mapCypherNodeIdentity(row)
  const confidence = asConfidence(row.confidence)
  if (identity === null || confidence === undefined) return null

  return {
    id: identity.id,
    labels: identity.labels,
    confidence,
    ...(identity.name !== undefined ? { name: identity.name } : {}),
    ...(identity.path !== undefined ? { path: identity.path } : {})
  }
}

/** Map one relationship cypher row; null when id/type/confidence is unusable. */
export function mapLowConfidenceRelationshipRow(
  row: Record<string, unknown>
): LowConfidenceRelationship | null {
  const id = asString(row.element_id)
  const type = asString(row.type)
  const confidence = asConfidence(row.confidence)
  if (id === undefined || type === undefined || confidence === undefined) return null

  const rel: LowConfidenceRelationship = { id, type, confidence }
  const sourceId = asString(row.source_id)
  if (sourceId !== undefined) rel.sourceId = sourceId
  const targetId = asString(row.target_id)
  if (targetId !== undefined) rel.targetId = targetId
  const sourceName = asString(row.source_name)
  if (sourceName !== undefined) rel.sourceName = sourceName
  const targetName = asString(row.target_name)
  if (targetName !== undefined) rel.targetName = targetName
  return rel
}
